import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "./app/store";
import { openapi, PostMultiplacerRoomSnapshotTrendByKeyApiArg } from "../openapi";
import { selectHttpProject } from "./DesignTab/designSlice";
import {
  selectActiveColorFilterItemFull,
  selectProjectNo,
  selectRoomNumbers,
} from "./Room/roomSelectors";
import { ITrendBucketConfig } from "@backend/src/schemas";

export const selectHttpCurrentUser = createSelector([(state: IRootState) => state], (state) => {
  const result = openapi.endpoints.getAuthMe.select(undefined)(state);
  return result.data ?? null;
});

export const selectIsUserAdmin = createSelector([selectHttpCurrentUser], (user) => {
  const ADMINS = ["per.strom@multiconsult.no"];
  if (!user || !user.email) {
    return false;
  }

  return ADMINS.includes(user.email);
});

export const selectTrendKeyForColorFilter = createSelector(
  [selectHttpProject, selectActiveColorFilterItemFull],
  (project, colorFilter): string | null => {
    if (!project || !colorFilter || !colorFilter.trendKeyPath) {
      return null;
    }
    return colorFilter.trendKeyPath;
  }
);

/**
 * NOTE: We use a tiny epsilon to convert "minExclusive" to a practical inclusive min.
 * This is only needed if you model exclusive bounds in the UI-spec.
 */
const EPS = 1e-9;

type ColorSpecLike = {
  id: string;
  range?: {
    minExclusive?: number;
    minInclusive?: number;
    maxInclusive?: number;
  };
};

/**
 * Lager bucketConfig basert på colorSpecs.range.
 * - Returnerer null hvis ingen ranges finnes (altså kategorisk trend).
 * - Returnerer {kind:"ranges"} hvis minst én range finnes.
 */
function buildBucketConfigFromColorSpecs(
  colorSpecs: ColorSpecLike[] | undefined
): ITrendBucketConfig | null {
  const specs = colorSpecs ?? [];

  const ranged = specs
    .filter(
      (s) =>
        s.range &&
        s.range.maxInclusive != null &&
        (s.range.minInclusive != null || s.range.minExclusive != null)
    )
    .map((s) => {
      const r = s.range!;
      const min = r.minInclusive != null ? r.minInclusive : (r.minExclusive as number) + EPS;

      const max = r.maxInclusive as number;

      return { id: s.id, min, max };
    });

  if (ranged.length === 0) return null;

  // Sort to make deterministic & nice for backend $cond-chain
  ranged.sort((a, b) => a.min - b.min);

  return {
    kind: "ranges",
    ranges: ranged,
  };
}

// ✅ build the RTK Query args in a selector
export const selectTrendByKeyArgsForColorFilter = createSelector(
  [selectProjectNo, selectActiveColorFilterItemFull, selectRoomNumbers],
  (
    projectNo,
    activeColorFilter,
    roomNumbers
  ): PostMultiplacerRoomSnapshotTrendByKeyApiArg | null => {
    const trendKey = activeColorFilter?.trendKeyPath;
    if (!projectNo || !trendKey || roomNumbers.length === 0) return null;

    const dateTo = new Date();
    const dateFrom = new Date(dateTo);
    dateFrom.setMonth(dateFrom.getMonth() - 6); // last ~6 months

    // ✅ Transform ranges from colorSpecs -> bucketConfig (only when numeric)
    const bucketConfig = buildBucketConfigFromColorSpecs(activeColorFilter?.colorSpecs);

    return {
      body: {
        projectNo,
        keyPath: trendKey,
        granularity: "week",
        excludeEmptyString: true,
        filter: {
          roomNumbers,
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
        },

        ...(bucketConfig ? { bucketConfig } : {}),
      },
    };
  }
);
